import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { User, AlertCircle, ChevronRight, ChevronLeft } from 'lucide-react';
import type { ClientPersona } from '@/data/insurance-scenarios';

interface ClientProfilePanelProps {
  persona: ClientPersona;
  language: 'en' | 'ko' | 'ja';
  isCollapsed: boolean;
  onToggle: () => void;
  showToggle?: boolean;
  fullWidth?: boolean;
}

export function ClientProfilePanel({
  persona,
  language,
  isCollapsed,
  onToggle,
  showToggle = true,
  fullWidth = false,
}: ClientProfilePanelProps) {
  const { t } = useTranslation();
  const isKorean = language === 'ko';
  const isJapanese = language === 'ja';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        ...(fullWidth ? {} : { width: isCollapsed ? '64px' : '288px' })
      }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className={`h-full bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col relative shadow-sm ${fullWidth ? 'w-full' : ''}`}
    >
      {/* Collapsed State - Icon, text, and button stacked vertically */}
      <AnimatePresence>
        {isCollapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="h-full flex flex-col items-center pt-6 pb-4 px-2 bg-white"
          >
            {/* User Icon */}
            <div className="w-10 h-10 rounded-full overflow-hidden shadow-sm border border-gray-100 mb-4">
              <img
                src={persona.avatarUrl || "https://dopmo1eihgbgm.cloudfront.net/68cf9e94d79b5fba547884ff/korean-male.webp"}
                alt="Client"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Text Label - Vertical */}
            <div className="flex flex-col items-center gap-1 mb-auto">
              {(isKorean ? '고객 정보' : isJapanese ? 'お客様情報' : 'Client Info').split('').map((char, idx) => (
                <span
                  key={idx}
                  className="text-[11px] font-semibold text-gray-400 leading-tight"
                >
                  {char}
                </span>
              ))}
            </div>

            {/* Toggle Button */}
            {showToggle && (
              <button
                onClick={onToggle}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-50 text-gray-400 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200"
                aria-label="Expand client info"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expanded State - Toggle button on the side */}
      <AnimatePresence>
        {!isCollapsed && showToggle && (
          <>
            {/* Toggle Button - Side position when expanded */}
            <button
              onClick={onToggle}
              className="absolute top-1/2 -translate-y-1/2 -right-3 z-10 bg-white text-gray-400 rounded-full p-1.5 shadow-md border border-gray-100 hover:text-blue-600 hover:border-blue-100 transition-all duration-200 group"
              aria-label="Collapse client info"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </AnimatePresence>

      {/* Expanded State - Full Content */}
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex-1 flex flex-col min-w-0 bg-white"
          >
            {/* Header - Clean White Sticky */}
            <div className="px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-100">
                  <img
                    src={persona.avatarUrl || "https://dopmo1eihgbgm.cloudfront.net/68cf9e94d79b5fba547884ff/korean-male.webp"}
                    alt="Client"
                    className="w-full h-full object-cover"
                  />
                </div>
                {t('insurance.clientInfo')}
              </h2>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-5 space-y-6">
              {/* Basic Info */}
              <div>
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  {isKorean ? '기본 정보' : isJapanese ? '基本情報' : 'Basic Profile'}
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-3">
                  <div>
                    <div className="text-lg font-bold text-gray-900">
                      {(isKorean || isJapanese) ? persona.nameKo : persona.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {persona.age}{isJapanese ? '歳' : '세'} • {(isKorean || isJapanese) ? persona.occupationKo : persona.occupation}
                    </div>
                  </div>

                  <div className="pt-3 border-t border-gray-200/50 grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-400 mb-0.5">{isKorean ? '가족' : isJapanese ? '家族' : 'Family'}</div>
                      <div className="text-sm font-medium text-gray-700">
                        {persona.family.children > 0
                          ? `${persona.family.children}${isJapanese ? '子' : '자녀'}`
                          : isJapanese ? '未婚' : isKorean ? '미혼' : 'Single'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400 mb-0.5">{isKorean ? '소득' : isJapanese ? '年収' : 'Income'}</div>
                      <div className="text-sm font-medium text-gray-700">
                        {(isKorean || isJapanese) ? persona.financial.annualIncomeKo : persona.financial.annualIncome}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Main Concerns */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-4 h-4 text-blue-500" />
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {t('insurance.mainConcerns')}
                  </div>
                </div>
                <ul className="space-y-2">
                  {((isKorean || isJapanese) ? persona.concernsKo : persona.concerns)
                    .slice(0, 3)
                    .map((concern, idx) => (
                      <li
                        key={idx}
                        className="text-sm text-gray-700 bg-white border border-gray-100 rounded-lg p-3 shadow-sm"
                      >
                        {concern}
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
